/**
 * Kompresi gambar di client (Canvas API) tanpa dependency tambahan.
 * Output default: JPEG agar ukuran lebih kecil untuk upload tablet/iPad.
 */

const DEFAULTS = {
  maxWidth: 1600,
  maxHeight: 1600,
  quality: 0.72,
  maxSizeKB: 500,
  mimeType: 'image/jpeg',
};

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i;

export function looksLikeImage(file) {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  // iOS/iPad sering kirim type kosong dari kamera
  if (!file.type && IMAGE_EXT_RE.test(file.name || '')) return true;
  if (!file.type && /^(image|photo|img|capture)/i.test(file.name || '')) return true;
  return false;
}

async function loadImageSource(file) {
  // createImageBitmap biasanya menghormati EXIF orientation (penting untuk kamera iPad)
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, cleanup: () => bitmap.close?.() };
    } catch (_) {
      // fallback ke Image element
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Gagal memuat gambar'));
      image.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      cleanup: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Gagal mengompres gambar'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
}

function getScaledSize(width, height, maxWidth, maxHeight) {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function buildFileName(originalName, mimeType) {
  const raw = (originalName || 'foto').trim() || 'foto';
  const base = raw.replace(/\.[^.]+$/, '').replace(/[^\w\-]+/g, '_') || 'foto';
  const ext = mimeType === 'image/png' ? 'png' : 'jpg';
  return `${base}-compressed.${ext}`;
}

/**
 * Pastikan File punya nama + ekstensi yang diterima server (.jpg/.jpeg/.png)
 */
export function ensureImageFileName(file, fallbackExt = 'jpg') {
  const name = file.name || `foto.${fallbackExt}`;
  if (IMAGE_EXT_RE.test(name)) {
    // normalisasi .jpeg sudah ok; server terima .jpg/.jpeg
    return file;
  }
  const safeName = `${name.replace(/\.[^.]+$/, '') || 'foto'}.${fallbackExt}`;
  return new File([file], safeName, {
    type: file.type || `image/${fallbackExt === 'jpg' ? 'jpeg' : fallbackExt}`,
    lastModified: file.lastModified || Date.now(),
  });
}

/**
 * @param {File} file
 * @param {Partial<typeof DEFAULTS>} options
 * @returns {Promise<File>}
 */
export async function compressImage(file, options = {}) {
  if (!file || !(file instanceof Blob)) {
    throw new Error('File tidak valid');
  }

  if (!looksLikeImage(file)) {
    throw new Error('File harus berupa gambar');
  }

  // GIF animasi / SVG jangan dipaksa kompres
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return ensureImageFileName(file, file.type === 'image/gif' ? 'gif' : 'svg');
  }

  const opts = { ...DEFAULTS, ...options };

  try {
    const loaded = await loadImageSource(file);
    const { width, height } = getScaledSize(
      loaded.width,
      loaded.height,
      opts.maxWidth,
      opts.maxHeight
    );

    if (!width || !height) {
      loaded.cleanup?.();
      throw new Error('Resolusi gambar tidak valid');
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(loaded.source, 0, 0, width, height);
    loaded.cleanup?.();

    let quality = opts.quality;
    let blob = await canvasToBlob(canvas, opts.mimeType, quality);

    while (blob.size / 1024 > opts.maxSizeKB && quality > 0.4) {
      quality = Math.max(0.4, quality - 0.1);
      blob = await canvasToBlob(canvas, opts.mimeType, quality);
    }

    return new File([blob], buildFileName(file.name, opts.mimeType), {
      type: opts.mimeType,
      lastModified: Date.now(),
    });
  } catch (err) {
    // Fallback: kirim file asli dengan nama yang aman (mis. HEIC gagal di-decode browser)
    console.warn('Kompresi gagal, memakai file asli:', err?.message || err);
    const fallback = ensureImageFileName(file, 'jpg');

    // Tolak jika tetap terlalu besar (>5MB sesuai limit server)
    if (fallback.size > 5 * 1024 * 1024) {
      throw new Error('Gambar terlalu besar dan gagal dikompres. Coba foto ulang dengan resolusi lebih rendah.');
    }

    return fallback;
  }
}

export function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Pastikan file siap diupload (kompres ulang jika masih besar / belum dari ImageUpload).
 * Aman dipanggil di submit FormData sebagai jaring pengaman.
 * @param {File|Blob|null|undefined} file
 * @returns {Promise<File|null>}
 */
export async function prepareImageForUpload(file) {
  if (!file) return null;
  if (!(file instanceof Blob)) return null;
  if (!looksLikeImage(file)) {
    throw new Error('File harus berupa gambar');
  }

  const alreadyCompressed =
    typeof file.name === 'string' &&
    file.name.includes('-compressed.') &&
    file.size <= DEFAULTS.maxSizeKB * 1024;

  if (alreadyCompressed) {
    return file instanceof File ? file : ensureImageFileName(file, 'jpg');
  }

  // Masih besar atau belum lewat kompresor → kompres sekarang
  return compressImage(file);
}
